import { createColumnHelper } from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import ActionMenu from '../components/ActionMenu'
import Modal from '../components/Modal'
import Table from '../components/dashboard/Table'
import StatusReportConfirmationModal from '../components/report/StatusReportConfirmationModal'
import { useUser } from '../contexts/UserContext'
import { projectService } from '../services/projectService'
import { statusReportService } from '../services/statusReportService'
import { tagService } from '../services/tagService'
import { workOrderService } from '../services/workOrderService'
import { type Project, ProjectStatus } from '../types/project'
import type { StatusReportListItem } from '../types/statusReport'
import type { Tag } from '../types/tag'
import type { WorkOrder } from '../types/workorder'
import { formatDate } from '../utils/date'

function getTagProjectId(tag: Tag): number | null {
  if (!tag.project) return null
  return typeof tag.project === 'number' ? tag.project : tag.project.id
}

function getTagWorkorderId(tag: Tag): number | null {
  if (!tag.workorder) return null
  return typeof tag.workorder === 'number' ? tag.workorder : tag.workorder.id
}

const ViewStatusReportsPage: React.FC = () => {
  const [statusReports, setStatusReports] = useState<StatusReportListItem[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useUser()

  // New report flow state
  const [showTagSelection, setShowTagSelection] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [completedTags, setCompletedTags] = useState<Tag[]>([])
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set())
  const [isLoadingTags, setIsLoadingTags] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  // Filter state
  const [projects, setProjects] = useState<Project[]>([])
  const [dispatches, setDispatches] = useState<WorkOrder[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('')
  const [selectedDispatchId, setSelectedDispatchId] = useState<number | ''>('')
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const [isLoadingDispatches, setIsLoadingDispatches] = useState(false)

  // Determine which tab we're on based on the URL
  const isSentTab = location.pathname.includes('/status-reports/sent')
  const isReceivedTab = location.pathname.includes('/status-reports/received')

  useEffect(() => {
    const fetchStatusReports = async () => {
      try {
        setIsLoading(true)
        let items: StatusReportListItem[]

        if (isSentTab) {
          // Fetch reports created by the current user
          const res = await statusReportService.listCreated({
            page,
            page_size: pageSize,
          })
          items = Array.isArray(res.items) ? res.items : []
          setTotalCount(res.count)
          setTotalPages(res.pages)
          console.log('Sent tab - Reports created by user:', items)
        } else {
          // Fetch all reports and filter to only show where current user is a recipient
          const res = await statusReportService.list({
            page,
            page_size: pageSize,
          })
          const allReports = Array.isArray(res.items) ? res.items : []
          setTotalCount(res.count)
          setTotalPages(res.pages)
          console.log('All reports from list():', allReports)
          console.log('Current user ID:', user?.id)

          items = allReports.filter((report) => {
            // Only show reports where current user is a recipient (not the creator)
            const isRecipient = report.recipients.some(
              (recipient) => recipient.id === user?.id,
            )
            const isNotCreator = report.created_by.id !== user?.id
            console.log(
              `Report ${report.id}: isRecipient=${isRecipient}, isNotCreator=${isNotCreator}, recipients=`,
              report.recipients.map((r) => r.id),
              'creator=',
              report.created_by.id,
            )
            return isRecipient && isNotCreator
          })
          console.log('Received tab - Reports where user is recipient:', items)
        }

        setStatusReports(items)
      } catch (error) {
        console.error('Failed to fetch status reports:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStatusReports()
  }, [isSentTab, user?.id, page, pageSize])

  const handleViewReport = (reportId: number) => {
    navigate(`/tag-report/${reportId}`)
  }

  const handleDeleteReport = async (reportId: number) => {
    if (!window.confirm('Are you sure you want to delete this status report?'))
      return
    try {
      await statusReportService.delete(reportId)
      setStatusReports((prev) => prev.filter((r) => r.id !== reportId))
      toast.success('Status report deleted')
    } catch (err) {
      console.error('Failed to delete report:', err)
      toast.error('Failed to delete status report')
    }
  }

  const filteredTags = useMemo(() => {
    let tags = completedTags
    if (selectedProjectId) {
      tags = tags.filter((t) => getTagProjectId(t) === selectedProjectId)
    }
    if (selectedDispatchId) {
      tags = tags.filter((t) => getTagWorkorderId(t) === selectedDispatchId)
    }
    return tags
  }, [completedTags, selectedProjectId, selectedDispatchId])

  const handleNewReport = async () => {
    setIsLoadingTags(true)
    setSelectedProjectId('')
    setSelectedDispatchId('')
    try {
      // Fetch completed tags and projects in parallel
      const [tags, projectsRes] = await Promise.all([
        tagService.getCompletedTags(),
        projectService.getProjects({
          status: ProjectStatus.ACCEPTED,
          page_size: 200,
        }),
      ])
      if (tags.length === 0) {
        toast.warning('No completed tags found')
        return
      }
      setCompletedTags(tags)
      setProjects(projectsRes.items || [])
      setSelectedTagIds(new Set(tags.map((t) => t.id)))
      setShowTagSelection(true)
    } catch (err) {
      console.error('Failed to fetch data:', err)
      toast.error('Failed to fetch completed tags')
    } finally {
      setIsLoadingTags(false)
    }
  }

  const handleProjectFilterChange = async (projectId: number | '') => {
    setSelectedProjectId(projectId)
    setSelectedDispatchId('')
    setDispatches([])

    if (projectId) {
      setIsLoadingDispatches(true)
      try {
        const res = await workOrderService.getWorkOrders({
          projectId: projectId,
          page_size: 200,
        })
        setDispatches(res.items || [])
      } catch (err) {
        console.error('Failed to fetch dispatches:', err)
      } finally {
        setIsLoadingDispatches(false)
      }
    }

    // Update selection: select all tags matching the new filter
    const filtered = completedTags.filter((t) => {
      if (projectId && getTagProjectId(t) !== projectId) return false
      return true
    })
    setSelectedTagIds(new Set(filtered.map((t) => t.id)))
  }

  const handleDispatchFilterChange = (dispatchId: number | '') => {
    setSelectedDispatchId(dispatchId)

    // Update selection: select all tags matching the new filter
    const filtered = completedTags.filter((t) => {
      if (selectedProjectId && getTagProjectId(t) !== selectedProjectId)
        return false
      if (dispatchId && getTagWorkorderId(t) !== dispatchId) return false
      return true
    })
    setSelectedTagIds(new Set(filtered.map((t) => t.id)))
  }

  const handleToggleTag = (tagId: number) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        next.add(tagId)
      }
      return next
    })
  }

  const handleToggleAll = () => {
    const filteredIds = new Set(filteredTags.map((t) => t.id))
    const allSelected = filteredTags.every((t) => selectedTagIds.has(t.id))
    if (allSelected) {
      // Deselect all filtered tags (keep others selected)
      setSelectedTagIds((prev) => {
        const next = new Set(prev)
        for (const id of filteredIds) next.delete(id)
        return next
      })
    } else {
      // Select all filtered tags (add to existing)
      setSelectedTagIds((prev) => new Set([...prev, ...filteredIds]))
    }
  }

  const handleProceedToConfirm = () => {
    if (selectedTagIds.size === 0) {
      toast.warning('Please select at least one tag')
      return
    }
    setShowTagSelection(false)
    setShowConfirmModal(true)
  }

  const handleConfirmReport = async (
    recipientUserIds: number[],
    notes: string,
  ) => {
    setIsGeneratingReport(true)
    try {
      const { statusReportId } = await statusReportService.generateReport({
        tag_ids: Array.from(selectedTagIds),
        recipient_user_ids:
          recipientUserIds.length > 0 ? recipientUserIds : null,
        notes: notes || null,
      })
      setShowConfirmModal(false)
      navigate(`/tag-report/${statusReportId}`)
    } catch (err) {
      console.error('Failed to generate report:', err)
      toast.error('Failed to generate report')
    } finally {
      setIsGeneratingReport(false)
    }
  }

  const columnHelper = createColumnHelper<StatusReportListItem>()

  const columns = [
    columnHelper.display({
      id: 'actions',
      cell: (info) => {
        const report = info.row.original
        const actions = [
          {
            label: 'View Report',
            icon: 'fa-solid fa-eye',
            onClick: () => handleViewReport(report.id),
            className: css(styles.viewReportButton),
          },
          ...(isSentTab
            ? [
                {
                  label: 'Delete',
                  icon: 'fa-solid fa-trash',
                  onClick: () => handleDeleteReport(report.id),
                  className: css(styles.deleteButton),
                },
              ]
            : []),
        ]

        return <ActionMenu actions={actions} />
      },
      header: () => <span>Actions</span>,
    }),
    columnHelper.accessor('label', {
      cell: (info) => info.getValue(),
      header: () => <span>Report ID</span>,
    }),
    columnHelper.accessor('sent_date', {
      cell: (info) => {
        const val = info.getValue()
        return val
          ? formatDate(val)
          : formatDate(info.row.original.created_date)
      },
      header: () => <span>Sent Date</span>,
    }),
    columnHelper.accessor('created_by', {
      cell: (info) => {
        const user = info.getValue()
        return `${user.first_name} ${user.last_name}`.trim() || user.email
      },
      header: () => <span>Sender</span>,
    }),
    columnHelper.accessor('recipients', {
      cell: (info) => {
        const recipients = info.getValue()
        if (recipients.length === 0) return '-'
        if (recipients.length === 1) {
          const recipient = recipients[0]
          return (
            `${recipient.first_name} ${recipient.last_name}`.trim() ||
            recipient.email
          )
        }
        return `${recipients.length} recipients`
      },
      header: () => <span>Recipients</span>,
    }),
  ]

  return (
    <div className={css(styles.statusReportsDashboard)}>
      <div className={css(styles.mainContent)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>
            {isSentTab ? 'Status Reports - Sent' : 'Status Reports - Received'}
          </h2>
          {isSentTab && (
            <button
              className={css(styles.newReportButton)}
              onClick={handleNewReport}
              disabled={isLoadingTags}
            >
              {isLoadingTags ? (
                <i className="fa-solid fa-spinner fa-spin" />
              ) : (
                <i className="fa-solid fa-plus" />
              )}{' '}
              New Status Report
            </button>
          )}
        </div>

        <div className={css(styles.tableContainer)}>
          <div className={css(styles.tableWrapper)}>
            {isLoading ? (
              <div className={css(styles.loadingContainer)}>
                <div className={css(styles.loadingSpinner)} />
                <p>Loading status reports...</p>
              </div>
            ) : (Array.isArray(statusReports) ? statusReports : []).length ===
              0 ? (
              <div className={css(styles.emptyContainer)}>
                <p>No status reports found.</p>
              </div>
            ) : (
              <Table
                data={statusReports}
                columns={columns}
                page={page}
                pages={totalPages}
                count={totalCount}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Tag Selection Modal */}
      {showTagSelection && (
        <Modal onClose={() => setShowTagSelection(false)}>
          <div className={css(styles.tagSelectionModal)}>
            <div className={css(styles.tagSelectionHeader)}>
              <h2 className={css(styles.tagSelectionTitle)}>
                Select Tags for Report
              </h2>
              <p className={css(styles.tagSelectionSubtext)}>
                {selectedTagIds.size} of {filteredTags.length} tags selected
                {(selectedProjectId || selectedDispatchId) &&
                  ` (${completedTags.length} total completed)`}
              </p>
            </div>

            {/* Filter Bar */}
            <div className={css(styles.filterBar)}>
              <div className={css(styles.filterGroup)}>
                <label className={css(styles.filterLabel)}>Project Terms</label>
                <select
                  className={css(styles.filterSelect)}
                  value={selectedProjectId}
                  onChange={(e) =>
                    handleProjectFilterChange(
                      e.target.value ? Number(e.target.value) : '',
                    )
                  }
                >
                  <option value="">All Projects</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label} — {p.job_site || p.job_id || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>
              <div className={css(styles.filterGroup)}>
                <label className={css(styles.filterLabel)}>Dispatch</label>
                <select
                  className={css(styles.filterSelect)}
                  value={selectedDispatchId}
                  onChange={(e) =>
                    handleDispatchFilterChange(
                      e.target.value ? Number(e.target.value) : '',
                    )
                  }
                  disabled={!selectedProjectId || isLoadingDispatches}
                >
                  <option value="">
                    {isLoadingDispatches
                      ? 'Loading...'
                      : !selectedProjectId
                        ? 'Select a project first'
                        : 'All Dispatches'}
                  </option>
                  {dispatches.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.label} — {d.material || 'No material'}
                    </option>
                  ))}
                </select>
              </div>
              {(selectedProjectId || selectedDispatchId) && (
                <button
                  className={css(styles.clearFilterButton)}
                  onClick={() => {
                    setSelectedProjectId('')
                    setSelectedDispatchId('')
                    setDispatches([])
                    setSelectedTagIds(new Set(completedTags.map((t) => t.id)))
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>

            <div className={css(styles.tagSelectionControls)}>
              <button
                className={css(styles.selectAllButton)}
                onClick={handleToggleAll}
              >
                {filteredTags.every((t) => selectedTagIds.has(t.id)) &&
                filteredTags.length > 0
                  ? 'Deselect All'
                  : 'Select All'}
              </button>
            </div>
            <div className={css(styles.tagSelectionList)}>
              {filteredTags.length === 0 ? (
                <div className={css(styles.noTagsMessage)}>
                  No completed tags match the current filters.
                </div>
              ) : (
                filteredTags.map((tag) => (
                  <label
                    key={tag.id}
                    className={css(
                      styles.tagSelectionItem,
                      selectedTagIds.has(tag.id) &&
                        styles.tagSelectionItemSelected,
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTagIds.has(tag.id)}
                      onChange={() => handleToggleTag(tag.id)}
                      className={css(styles.tagCheckbox)}
                    />
                    <div className={css(styles.tagSelectionInfo)}>
                      <span className={css(styles.tagSelectionLabel)}>
                        {tag.label}
                      </span>
                      <span className={css(styles.tagSelectionDetail)}>
                        {tag.title || tag.material || 'Untitled'}
                        {tag.assigned_user
                          ? ` · ${tag.assigned_user.first_name} ${tag.assigned_user.last_name}`
                          : ''}
                      </span>
                    </div>
                    <span className={css(styles.tagSelectionStatus)}>
                      {tag.status}
                    </span>
                  </label>
                ))
              )}
            </div>
            <div className={css(styles.tagSelectionActions)}>
              <button
                className={css(styles.cancelButton)}
                onClick={() => setShowTagSelection(false)}
              >
                Cancel
              </button>
              <button
                className={css(styles.proceedButton)}
                onClick={handleProceedToConfirm}
                disabled={selectedTagIds.size === 0}
              >
                Continue ({selectedTagIds.size} tags)
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <StatusReportConfirmationModal
          isOpen={showConfirmModal}
          completedTags={completedTags
            .filter((t) => selectedTagIds.has(t.id))
            .map((t) => ({
              id: t.id,
              label: t.label,
              status: t.status,
              title: t.title,
            }))}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmReport}
          isSubmitting={isGeneratingReport}
          suggestedRecipientIds={[
            ...new Set(
              completedTags
                .filter(
                  (t) => selectedTagIds.has(t.id) && t.contractor_user?.id,
                )
                .map((t) => t.contractor_user?.id),
            ),
          ]}
        />
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  statusReportsDashboard: {
    display: 'flex',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    width: 'calc(100vw - 280px)',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    '@media (max-width: 1024px)': {
      padding: '12px',
      width: '100vw',
    },
  },
  mainContent: {
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: '20px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '12px',
      padding: '12px',
    },
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    color: '#1F2937',
  },
  tableContainer: {
    borderRadius: '8px',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#F9FAFB',
    overflowX: 'hidden',
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: '#F9FAFB',
    display: 'block',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    color: '#6B7280',
  },
  loadingSpinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #E5E7EB',
    borderTop: '3px solid #3B82F6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    color: '#6B7280',
  },
  newReportButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#059669',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: '#047857',
    },
    ':disabled': {
      backgroundColor: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
  tagSelectionModal: {
    padding: '32px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    margin: '0 auto',
    maxWidth: '640px',
    width: '90vw',
  },
  tagSelectionHeader: {
    marginBottom: '16px',
  },
  tagSelectionTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 4px',
  },
  tagSelectionSubtext: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    marginBottom: '16px',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: '180px',
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#111827',
    backgroundColor: 'white',
    cursor: 'pointer',
    ':focus': {
      outline: 'none',
      borderColor: '#059669',
      boxShadow: '0 0 0 2px rgba(5, 150, 105, 0.2)',
    },
    ':disabled': {
      backgroundColor: '#F3F4F6',
      color: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
  clearFilterButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6B7280',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-end',
    ':hover': {
      backgroundColor: '#F3F4F6',
      color: '#374151',
    },
  },
  noTagsMessage: {
    padding: '32px',
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '14px',
  },
  tagSelectionControls: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '12px',
  },
  selectAllButton: {
    padding: '6px 12px',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  tagSelectionList: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  tagSelectionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: '1px solid #F3F4F6',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  tagSelectionItemSelected: {
    backgroundColor: '#F0FDF4',
    ':hover': {
      backgroundColor: '#ECFDF5',
    },
  },
  tagCheckbox: {
    width: '18px',
    height: '18px',
    accentColor: '#059669',
    cursor: 'pointer',
    flexShrink: 0,
  },
  tagSelectionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    minWidth: 0,
  },
  tagSelectionLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
  },
  tagSelectionDetail: {
    fontSize: '12px',
    color: '#6B7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tagSelectionStatus: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#065F46',
    backgroundColor: '#D1FAE5',
    padding: '3px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  tagSelectionActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'space-between',
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  proceedButton: {
    backgroundColor: '#059669',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#047857',
    },
    ':disabled': {
      backgroundColor: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
  deleteButton: {
    padding: '6px 12px',
    color: '#FFFFFF',
    background: '#DC2626',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
    maxWidth: '120px',
    ':hover': {
      background: '#B91C1C',
    },
  },
  viewReportButton: {
    padding: '6px 12px',
    color: '#FFFFFF',
    background: '#7C3AED',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
    maxWidth: '120px',
    ':hover': {
      background: '#6D28D9',
    },
  },
})

export default ViewStatusReportsPage
